import { Offcanvas } from "react-bootstrap";
import { ContractorData } from "components/utils/contacts";
import { Button } from "components/Button";
import { isEmpty, omit, trim } from "lodash";
import { useContext, useState } from "react";
import { DataGridWithFilter } from "components/DataGrid";
import { DataGridFilter } from "components/DataGrid/DataGridFilter";
import { DataGridPaging } from "components/DataGrid/DataGridPaging";
import { DataGridHeader } from "components/DataGrid/DataGridHeader";
import { DataGridCellValue, DataGridColumnConfig, DataGridConfig, DataGridRowConfig } from "components/DataGrid/types";
import { DataGridBody } from "components/DataGrid/DataGridBody";
import { formatCellContent, getColumnSortState } from "components/DataGrid/utils";
import { SubmitButton } from "components/Button/SubmitButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGridCellBadges } from "components/DataGrid/components/DataGridCellBadges";
import { DataGridCellCopy } from "components/DataGrid/components/DataGridCellCopy";
import { DataGridHead } from "components/DataGrid/DataGridHead";
import useMediaQuery from "components/utils/useMediaQuery";
import cn from "classnames";
import { useProgramContractorSearchParams } from "components/Page/ContractorMapPage/getProgramContractors";
import { AppContext } from "components/App/AppContext";
import { useColor } from "components/utils/color";
import Color from "color";
import { useContractorLabel } from "components/JsonForm/theme/widgets/ApplicationContactsWidget/useContractorLabel";
import { useApplicationContractors } from "components/JsonForm/theme/widgets/ApplicationContactsWidget/useApplicationContractors";
import { ContactorSearchColumnKeys, ContractorsResponse } from "components/JsonForm/theme/widgets/ApplicationContactsWidget/types";
import { Icon } from "components/Icon";
import { ButtonVariant } from "react-bootstrap/esm/types";

export const ContractorSearch = ({
    applicationNumber,
    hide,
    onSelect,
    isContractorRequired,
    variant,
    ariaDescribedBy,
}: ContractorSearchProps) => {
    const [showList, setShowList] = useState(false);

    const contractorLabel = useContractorLabel();
    const isMobile = useMediaQuery("(max-width: 768px)");

    const pageSize = 20;
    const [searchAttr, setSearchAttr] = useState<string>("");
    const [sortBy, setSort] = useState<string>("");
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [contractorData, isLoading] = useApplicationContractors(
        showList ? applicationNumber : undefined,
        pageNumber,
        pageSize,
        searchAttr,
        sortBy,
        sortAsc
    );

    const isLoadingList = showList && isEmpty(contractorData) && isLoading;

    const onSortChange = (key: string, forceAsc?: boolean) => {
        if (forceAsc !== undefined) {
            setSort(key);
            setSortAsc(forceAsc);
            return;
        }

        if (sortBy === key) {
            setSortAsc((prev) => !prev);
            return;
        }

        setSort(key);
        setSortAsc(true);
    };

    const onFilterChange = (newValue: string) => {
        if (newValue !== searchAttr) {
            setSearchAttr(newValue);
            setPageNumber(1);
        }
    };

    const onSelectContractor = (contractor: ContractorData) => {
        setShowList(false);
        setSearchAttr("");
        setSort("");
        setPageNumber(1);
        onSelect(contractor);
    };

    const onCancel = () => {
        setShowList(false);
        setSearchAttr("");
        setSort("");
        setPageNumber(1);
    };

    if (hide) {
        return null;
    }

    if (showList && !isLoadingList) {
        return (
            <ContractorList
                contractorData={contractorData}
                pageSize={pageSize}
                pageNumber={pageNumber}
                sortBy={sortBy}
                sortAsc={sortAsc}
                searchAttr={searchAttr}
                contractorLabel={contractorLabel}
                onSelectContractor={onSelectContractor}
                onSortChange={onSortChange}
                onFilterChange={onFilterChange}
                onPageChange={setPageNumber}
                onCancel={onCancel}
            />
        );
    }

    return (
        <div
            className={cn("d-flex gap-2", {
                "flex-column align-items-start": isMobile,
                "align-items-center": !isMobile,
            })}
        >
            <SubmitButton
                className="text-decoration-none"
                variant={variant ?? "secondary"}
                onClick={() => setShowList(true)}
                isSubmitting={isLoadingList}
                spinnerText="Loading contractor list..."
                aria-describedby={ariaDescribedBy}
            >
                <Icon className="me-2" icon={["fal", "magnifying-glass"]} />
                Search for a {contractorLabel}
            </SubmitButton>
            {isContractorRequired && <span>* Required</span>}
        </div>
    );
};

const ContractorListCopy = (column: DataGridColumnConfig, value: DataGridCellValue, row: DataGridRowConfig) => {
    const columnCopyMapper: Record<string, boolean> = {
        email: true,
        phone: true,
    };

    const record = columnCopyMapper[column.name.toLowerCase().replace(" ", "_")];
    if (!record) return formatCellContent({ ...column, renderer: undefined }, value, row);

    return <DataGridCellCopy column={column} row={row} />;
};

const ContractorList = ({
    contractorData,
    pageSize,
    pageNumber,
    sortBy,
    sortAsc,
    searchAttr,
    contractorLabel,
    onSelectContractor,
    onSortChange,
    onFilterChange,
    onPageChange,
    onCancel,
}: {
    contractorData?: ContractorsResponse;
    pageSize: number;
    pageNumber: number;
    sortBy: string;
    sortAsc: boolean;
    searchAttr: string;
    contractorLabel: string;
    onSelectContractor: (contractor: ContractorData) => void;
    onSortChange: (key: string) => void;
    onFilterChange: (newValue: string) => void;
    onPageChange: (pageNumber: number) => void;
    onCancel: () => void;
}) => {
    const totalRecords = contractorData?.totalRecords ?? 0;
    const pagesCount = Math.ceil(totalRecords / pageSize);
    const {
        requirements: { cmpServicesLabel, cmpTechnologiesLabel },
    } = useContext(AppContext);
    const isMobile = useMediaQuery("(max-width: 768px)");
    const { programNumber } = useContext(AppContext);
    const [contractorSearchParams] = useProgramContractorSearchParams(programNumber);
    const [itemsCounter, setItemsCounter] = useState<Record<number, boolean>>(
        Object.fromEntries(contractorData?.items.map((_, i) => [i, false]) ?? [])
    );

    const mobileConfig = {
        title: ContactorSearchColumnKeys.fullName,
        subtitle: ContactorSearchColumnKeys.fullAddress,
        columns: ["technologies", "services"],
    };

    const onMail = (email: string) => {
        window.location.href = `mailto:${email}`;
    };

    const onCall = (phone: string) => {
        window.location.href = `tel:${phone}`;
    };

    const rows: DataGridRowConfig[] = (contractorData?.items ?? []).map((item: any, index) => ({
        index,
        data: {
            ...item,
            // Add empty space after commas to prevent long words in data grid.
            technologies: (item.technologies ?? "").split(",").map(trim).join(", "),
            services: (item.services ?? "").split(",").map(trim).join(", "),
        },
        actions: [
            {
                content: (row, index: number) => (
                    <div key={index} className="d-flex gap-2 align-items-stretch">
                        <Button
                            aria-label={`Select Company ${row.data.company}`}
                            variant={!isMobile ? "link" : "outline-secondary"}
                            className="text-decoration-none d-flex align-items-center gap-2"
                            onClick={() => onSelectContractor(item)}
                        >
                            {isMobile && <span>Select Company </span>}
                            <FontAwesomeIcon icon={["far", "arrow-right"]} />
                        </Button>
                        {isMobile && item?.phone && (
                            <Button
                                variant="outline-secondary"
                                className="d-flex gap-2 align-items-center"
                                onClick={() => onCall(item.phone)}
                            >
                                <FontAwesomeIcon icon={["fal", "phone"]} size="lg" />
                            </Button>
                        )}
                        {isMobile && item?.email && (
                            <Button
                                variant="outline-secondary"
                                className="d-flex gap-2 align-items-center"
                                onClick={() => onMail(item.email)}
                            >
                                <FontAwesomeIcon icon={["fal", "envelope"]} size="lg" />
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        mobileConfig,
        rendererPosHeader: (rowIndex: number) => (
            <Button
                className="ms-auto"
                variant="link"
                title="Toggle column badge"
                aria-label="Toggle column badge"
                onClick={() => setItemsCounter((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }))}
            >
                <FontAwesomeIcon icon={itemsCounter[rowIndex] ? "angle-up" : "angle-down"} size="lg" />
            </Button>
        ),
    }));

    const getColumnSelectValues = (column: DataGridColumnConfig) => {
        const technologies = contractorSearchParams?.technologies;
        const services = contractorSearchParams?.services;
        if (!technologies?.length || !services?.length) return;

        const mapper = {
            [ContactorSearchColumnKeys.technologies]: technologies.map((i) => ({
                title: `${i.parameterValue}`,
                value: `${i.parameterValue}`,
            })),
            [ContactorSearchColumnKeys.services]: services.map((i) => ({
                title: `${i.parameterValue}`,
                value: `${i.parameterValue}`,
            })),
        };

        return mapper[column.key as keyof typeof mapper];
    };

    const ContractorListBadges = (_: DataGridColumnConfig, value: DataGridCellValue, row: DataGridRowConfig) => {
        const defaultItemsAmount = isMobile ? 0 : 2;
        const primaryColor = useColor("primary");
        const backgroundColor = Color(primaryColor).mix(Color("white"), 0.8);

        if (typeof value !== "string") return value;
        const splitedValue = value.split(",");
        const items = splitedValue.map((i) => i.toUpperCase());
        const visibleItems = items.slice(0, itemsCounter[row.index] ? items.length : defaultItemsAmount);

        return (
            <DataGridCellBadges
                items={items}
                visibleItems={visibleItems}
                badgeStyle={{ maxWidth: "200px", backgroundColor: backgroundColor.hex() }}
                badgeClassName="text-truncate fw-bold"
            />
        );
    };

    const getColumnRenderer = (column: DataGridColumnConfig) => {
        const mapper: Record<string, typeof ContractorListBadges | typeof ContractorListCopy> = {
            technologies: ContractorListBadges,
            services: ContractorListBadges,
            phone: ContractorListCopy,
            email: ContractorListCopy,
        };

        return mapper[column.columnname.toLocaleLowerCase()];
    };

    const columns: DataGridColumnConfig[] = getCustomerSearchColumns(cmpTechnologiesLabel, cmpServicesLabel).flatMap((c) => {
        const sort: "ASC" | "DESC" | undefined = getColumnSortState(c, sortBy, sortAsc);
        const renderer = getColumnRenderer(c);
        const selectValues = getColumnSelectValues(c);

        const bodyColumns = [...mobileConfig.columns.map((i) => i.toLowerCase()), "phone", "email"];
        const headerColumns = [mobileConfig.title, mobileConfig.subtitle].map((i) => i.toLowerCase());
        const bodyColumnsIncludes = bodyColumns.includes(c.columnname.toLowerCase());
        const headerColumnsIncludes = headerColumns.includes(c.key.toLowerCase() as any);

        if (isMobile && !bodyColumnsIncludes && !headerColumnsIncludes) {
            return [];
        }

        return [
            {
                ...c,
                sort,
                renderer,
                selectValues,
            },
        ];
    });

    // Remove word wrap classes from header.
    const headerColumns = columns.map((c) => omit(c, "className"));

    const gridConfig: DataGridConfig = {
        columns,
        pageNumber,
        pagesCount,
        pageSize,
        totalRecords,
        filter: searchAttr,
    };

    return (
        <Offcanvas
            className={cn("overflow-y-auto contractor-list", { "offcanvas-75 p-3": !isMobile })}
            show
            placement="end"
            backdrop="false"
            onHide={onCancel}
            aria-labelledby="contractor-search-title"
        >
            <Offcanvas.Header className={cn({ "px-0": !isMobile, "px-2": isMobile })} closeButton>
                <Offcanvas.Title id="contractor-search-title" className="fs-3">
                    {contractorLabel} Search
                </Offcanvas.Title>
            </Offcanvas.Header>
            {!isMobile && (
                <p style={{ color: "#706B85" }}>
                    <FontAwesomeIcon icon={["fal", "circle-info"]} className="me-2" />
                    Use * as a wildcard in your search to find potential matches (e.g., ABC123*).
                </p>
            )}
            <Offcanvas.Body className={cn("d-flex flex-column p-0", { "overflow-y-visible": isMobile })}>
                <DataGridWithFilter
                    onFilterChange={onFilterChange}
                    onSortChange={onSortChange}
                    gridConfig={gridConfig}
                    className="applications-contractor-list data-grid-in-modal"
                    withWrapper={false}
                >
                    {({
                        isFilterOpen,
                        isSortOpen,
                        isFilterChanged,
                        isFilterEmpty,
                        toggleFilter,
                        toggleSort,
                        filterSelected,
                        filter,
                        getFilterValue,
                        onFilterValueChange,
                        onFilterValueKeyDown,
                        onFieldClean,
                        onFilterSubmit,
                        onFilterClear,
                        onSortClear,
                        onFilterInputFocus,
                        setFilterInputRef,
                    }) => (
                        <>
                            <DataGridHead className="position-sticky sticky-top">
                                <DataGridHeader
                                    toggleFilter={toggleFilter}
                                    toggleSort={toggleSort}
                                    isFilterOpen={isFilterOpen}
                                    isSortOpen={isSortOpen}
                                    isFilterEmpty={isFilterEmpty}
                                    columns={headerColumns}
                                    onSortChange={onSortChange}
                                    filter={filter}
                                    sortBy={sortBy}
                                    sortAsc={sortAsc}
                                    totalRecords={totalRecords}
                                    onFilterClear={onFilterClear}
                                    onSortClear={onSortClear}
                                    isFilterChanged={isFilterChanged}
                                    getFilterValue={getFilterValue}
                                    onFieldClean={onFieldClean}
                                    onFilterSubmit={onFilterSubmit}
                                    onFilterInputFocus={onFilterInputFocus}
                                />
                                <DataGridFilter
                                    setFilterInputRef={setFilterInputRef}
                                    toggleFilter={toggleFilter}
                                    toggleSort={toggleSort}
                                    isFilterOpen={isFilterOpen}
                                    isSortOpen={isSortOpen}
                                    isFilterEmpty={isFilterEmpty}
                                    columns={columns}
                                    onFilterChange={onFilterChange}
                                    filter={filter}
                                    sortBy={sortBy}
                                    sortAsc={sortAsc}
                                    filterSelected={filterSelected}
                                    getFilterValue={getFilterValue}
                                    onFilterValueChange={onFilterValueChange}
                                    onFilterValueKeyDown={onFilterValueKeyDown}
                                    onFilterClear={onFilterClear}
                                    onSortClear={onSortClear}
                                    onFieldClean={onFieldClean}
                                    onFilterSubmit={onFilterSubmit}
                                    onFilterInputFocus={onFilterInputFocus}
                                    onSortChange={onSortChange}
                                />
                            </DataGridHead>
                            <DataGridBody rows={rows} columns={columns} isFilterEmpty={isFilterEmpty} onFilterClear={onFilterClear} />
                        </>
                    )}
                </DataGridWithFilter>
                {totalRecords > 10 && (
                    <DataGridPaging
                        className={cn({ "position-sticky sticky-bottom p-2 border-top": isMobile, "py-3": !isMobile })}
                        style={{ backgroundColor: "var(--table-background-color)" }}
                        pageNumber={pageNumber}
                        pagesCount={pagesCount}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

const getCustomerSearchColumns: (technologiesLabel?: string, servicesLabel?: string) => DataGridColumnConfig[] = (
    technologiesLabel = "Technologies",
    servicesLabel = "Services"
) => [
    {
        key: "fullName",
        name: "Company",
        columnname: "Company",
        active: "true",
        filter: "true",
        className: "w-lg-15 text-break",
    },
    {
        key: "fullAddress",
        name: "Address",
        columnname: "Address",
        active: "true",
        filter: "true",
        className: "w-lg-15 text-break",
    },
    {
        key: "phone",
        name: "Phone",
        columnname: "Phone",
        active: "true",
        filter: "true",
    },
    {
        key: "email",
        name: "Email",
        columnname: "Email",
        active: "true",
        filter: "true",
        className: "text-break",
        datatype: "email",
    },
    {
        key: "technologies",
        name: technologiesLabel.substring(0, 25),
        columnname: "Technologies",
        active: "true",
        filter: "true",
        className: "w-lg-15 text-break",
    },
    {
        key: "services",
        name: servicesLabel.substring(0, 25),
        columnname: "Services",
        active: "true",
        filter: "true",
        className: "w-lg-15 text-break",
    },
];

interface ContractorSearchProps {
    hide?: boolean;
    applicationNumber: string;
    onSelect: (contractor: any) => void;
    isContractorRequired?: boolean;
    setFilterSelected?: React.Dispatch<React.SetStateAction<string>>;
    variant?: ButtonVariant;
    ariaDescribedBy?: string;
}
